import React, { useState } from 'react';
import useModalStore from '../../stores/modalStore';
import { epochToIso8601 } from "../../utils/formatting";
import getFirebaseAuthToken from "../../services/fireBaseAuthenticationService";
import axios from "axios";
import { BACKEND_CALL_TIMEOUT, BACKEND_URL, LOCATION_NAME_TO_ID } from "../../utils/constants";
import { loggedAlert } from "../../services/eventService";
import useUserStore from "../../stores/userStore";
import useEventStore from "../../stores/eventStore";
import LoadingSpinner from "../../LoadingSpinner";
import SickEventsTable from "../sections/SickEventsTable";

const SickModal = () => {
    const { selectedEvent, setSelectedEvent, updateSelectedEventTimestamp, updateSelectedEventLocationId } = useEventStore();
    const show = useModalStore(state => state.showSickModal);
    const { closeModal } = useModalStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { currentUser, setEmployeeEvents, setRequestList } = useUserStore();

    const logSickEvent = async () => {
        if (!selectedEvent?.event_timestamp) {
            loggedAlert("Please select a date for the sick day.", currentUser.user_id, selectedEvent?.location_id);
            return;
        }

        setIsSubmitting(true);
        try {
            const token = await getFirebaseAuthToken();

            const payload = {
                event_type: "sick",
                event_timestamp: selectedEvent.event_timestamp,
                insert_timestamp: Date.now(),
                notes: selectedEvent?.notes || "",
                created_by: currentUser.user_id,
                user_id: show ? currentUser.user_id : selectedEvent.user_id,
                location_id: selectedEvent.location_id,
            };

            const response = await axios.post(`${BACKEND_URL}/timesheet`, payload, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: BACKEND_CALL_TIMEOUT
            });

            if (response.status === 200) {
                const newlyAddedEvent = response.data;
                setEmployeeEvents(currentUser.user_id, prev => [newlyAddedEvent, ...prev]);
                setRequestList(prev => [newlyAddedEvent, ...prev]);
                closeModal("sickModal");
            } else {
                loggedAlert(`Error: ${response.data.error}`, currentUser?.user_id, selectedEvent?.location_id);
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
            loggedAlert(`An error occurred while adding the sick event: ${errorMessage}`, currentUser?.user_id, selectedEvent?.location_id);
        } finally {
            setSelectedEvent(prev => ({ ...prev, notes: "" }));
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800">Sick Leave</h3>
                    <button
                        onClick={() => {
                            setSelectedEvent(prev => ({ ...prev, notes: "", event_timestamp: Date.now() }));
                            closeModal("sickModal");
                        }}
                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    <div className="flex flex-col sm:flex-row gap-0">
                        {/* Left column: SickEventsTable */}
                        <div className="w-full sm:w-1/2 max-h-[60vh] overflow-y-auto border-r border-gray-100 p-4">
                            <SickEventsTable />
                        </div>

                        {/* Right column: Form */}
                        <div className="w-full sm:w-1/2 p-6">
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Date & Time
                                    <input
                                        className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                        required
                                        type="datetime-local"
                                        value={selectedEvent?.event_timestamp
                                            ? epochToIso8601(selectedEvent.event_timestamp)
                                            : ''
                                        }
                                        onChange={updateSelectedEventTimestamp}
                                    />
                                </label>

                                <label className="block text-sm font-medium text-gray-700">
                                    Location
                                    <select
                                        className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                        value={selectedEvent.location_id}
                                        onChange={updateSelectedEventLocationId}
                                    >
                                        <option value="" disabled>Select a location</option>
                                        {Object.entries(LOCATION_NAME_TO_ID)
                                            .filter(([_, id]) => id !== null)
                                            .map(([name, id]) => (
                                                <option key={id} value={id}>{name}</option>
                                            ))}
                                    </select>
                                </label>

                                <label className="block text-sm font-medium text-gray-700">
                                    Notes
                                    <textarea
                                        className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm resize-y min-h-[60px]"
                                        value={selectedEvent?.notes || ''}
                                        placeholder="Optional notes..."
                                        onChange={(e) =>
                                            setSelectedEvent({ ...selectedEvent, notes: e.target.value })
                                        }
                                    ></textarea>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedEvent(prev => ({ ...prev, notes: "", event_timestamp: Date.now() }));
                                        closeModal("sickModal");
                                    }}
                                    className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                                >
                                    Cancel
                                </button>
                                {isSubmitting ? (
                                    <div className="px-4 py-2 flex items-center"><LoadingSpinner small /></div>
                                ) : (
                                    <button
                                        onClick={logSickEvent}
                                        disabled={isSubmitting}
                                        className="!px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md !transition-all"
                                    >
                                        Submit
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SickModal;
